import React, { useMemo, useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { CellInfo } from 'react-table';
import { observer } from 'mobx-react';
import PageContainer from 'src/components/PageContainer';
import PageContent from 'src/components/PageContent';
import NavBar from '../NavBar';
import TabNav from 'src/components/TabNav';
import Table from 'src/components/Table';
import Button from 'src/components/Button';
import DateComponent from 'src/components/DateComponent';
import Flex from 'src/components/Flex';
import Pagination from 'src/components/Table/Pagination';
import { useDocumentScans, DocumentScan } from 'src/queries/useDocumentScans';
import styles from './styles.module.scss';
import FacilityDropDown from 'src/components/FacilityDropDown';
import FilterStore from 'src/stores/FilterStore';
import { FilterQueryParams } from 'src/utils/types';
import { ROUTES } from 'src/utils/constants';
import StatusBadge, { StatusBadgeColors } from 'src/components/StatusBadge';

const TABS = ['Successful', 'Unsuccessful'];
const FILTER_PAGE = 'documentScansPage';

function DocumentScans() {
    const history = useHistory();
    const [selectedFacility, setSelectedFacility] = useState<number | null>(null);
    const [selectedTab, setSelectedTab] = useState(TABS[0]);
    const filterStore = useContext(FilterStore);
    const documentScansFilters: FilterQueryParams = filterStore[FILTER_PAGE] || { pagination: { page: 1, limit: 25 } };

    // Determine hasEncounter filter based on selected tab
    const hasEncounter = selectedTab === TABS[0] ? true : false;

    // Reset pagination to page 1 when tab changes
    useEffect(() => {
        filterStore.setFilters(FILTER_PAGE, {
            ...documentScansFilters,
            pagination: { ...(documentScansFilters.pagination || { page: 1, limit: 25 }), page: 1 },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTab]);

    const { data: queryResult, isLoading, error } = useDocumentScans(selectedFacility || 0, {
        hasEncounter,
        pagination: documentScansFilters.pagination || { page: 1, limit: 25 },
    });

    const documentScans = queryResult?.data || [];
    const totalCount = queryResult?.count || 0;

    const handleFacilityChange = (facility: any) => {
        setSelectedFacility(facility?.value || null);
        // Reset pagination when facility changes
        filterStore.setFilters(FILTER_PAGE, {
            ...documentScansFilters,
            pagination: { ...(documentScansFilters.pagination || { page: 1, limit: 25 }), page: 1 },
        });
    };

    const handlePaginationFilterChange = (page: number) => {
        filterStore.setFilters(FILTER_PAGE, {
            ...documentScansFilters,
            pagination: { ...(documentScansFilters.pagination || { page: 1, limit: 25 }), page },
        });
    };

    const assetColumns = useMemo(() => {
        return [
            {
                Header: 'ASSET TYPE',
                accessor: 'assetType',
                minWidth: 150,
                Cell: (props: CellInfo) => {
                    const type = props.value as string;
                    return type.replace('_', ' ').toUpperCase();
                },
            },
            {
                Header: 'STATUS',
                accessor: 'status',
                minWidth: 120,
                Cell: (props: CellInfo) => {
                    const status = props.value as string;
                    const statusColor =
                        status === 'uploaded'
                            ? StatusBadgeColors.Green
                            : status === 'failed'
                            ? StatusBadgeColors.Red
                            : StatusBadgeColors.Yellow;
                    return <StatusBadge status={statusColor} text={status.toUpperCase()} />;
                },
            },
            {
                Header: 'ERROR MESSAGE',
                accessor: 'errorMessage',
                minWidth: 200,
                Cell: (props: CellInfo) => props.value || '—',
            },
        ];
    }, []);

    const columns = useMemo(() => {
        const baseColumns = [
            {
                Header: 'DOCUMENT SCAN ID',
                accessor: 'documentScanId',
                minWidth: 200,
            },
            {
                Header: 'Assets',
                accessor: 'assets',
                minWidth: 200,
                Cell: (props: CellInfo) => props.value?.length || 0,
            },
            {
                Header: 'CREATED',
                accessor: 'createdAt',
                minWidth: 150,
                Cell: (props: CellInfo) => <DateComponent date={props.value} showDateTime={true} />,
            },
            {
                Header: 'ACTIONS',
                minWidth: 150,
                style: { paddingVertical: 0, paddingHorizontal: 20 },
                Cell: (props: CellInfo) => (
                    <Flex direction="row" align="center">
                        <Button
                            type="small"
                            text="View"
                            onClick={() => handleView(props.original)}
                            className={styles.actionButton}
                            data-test-id="view-document-scan-button"
                        />
                    </Flex>
                ),
            },
        ];

        if (selectedTab === TABS[0]) {
            return [
                {
                    Header: 'ENCOUNTER NUMBER',
                    id: 'encounterNumber',
                    accessor: (row: DocumentScan) => row.encounter?.enctrNo || null,
                    minWidth: 150,
                    Cell: (props: CellInfo) => props.value || '—',
                },
                ...baseColumns,
            ];
        }

        return baseColumns;
    }, [selectedTab]);

    const SubComponent = (props: { original: DocumentScan; nestingLevel?: number; index?: number }) => {
        const { original } = props;
        const assets = original.assets || [];

        if (assets.length === 0) {
            return (
                <div className={styles.expandedRowContent}>
                    <div className={styles.emptyAssetsMessage}>No assets found for this document scan.</div>
                </div>
            );
        }

        return (
            <div className={styles.expandedRowContent}>
                <Table columns={assetColumns} data={assets} isLoading={false} showPagination={false} />
            </div>
        );
    };

    function handleView(scan: DocumentScan) {
        history.push(ROUTES.getString(ROUTES.DocumentScan, undefined, { documentScanId: scan.documentScanId }));
    }

    return (
        <PageContainer>
            <NavBar />
            <PageContent>
                <Flex direction="column" className={styles.container}>
                    <Flex direction="column" className={styles.header}>
                        <h1 className={styles.pageTitle}>Document Scans</h1>
                        <p className={styles.pageSubtitle}>Manage document scans for different facilities</p>
                    </Flex>
                    <Flex direction="row" align="center" justify="between" className={styles.controlsRow}>
                        <Flex className={styles.filterWrap}>
                            <FacilityDropDown
                                onChange={handleFacilityChange}
                                selectedValue={selectedFacility}
                                allowEmpty={false}
                                placeholder="Filter by Facility"
                            />
                        </Flex>
                    </Flex>
                </Flex>
                <div className={styles.tabContainer}>
                    <TabNav
                        tabs={TABS}
                        onTabClick={(tab: string) => {
                            setSelectedTab(tab);
                        }}
                    />
                </div>
                <Table
                    columns={columns}
                    data={documentScans}
                    isLoading={isLoading}
                    showPagination={false}
                    pageSize={documentScansFilters.pagination?.limit || 25}
                    SubComponent={SubComponent}
                />
                {documentScans.length > 0 && (
                    <Pagination
                        pages={Math.ceil(totalCount / (documentScansFilters.pagination?.limit || 25)) || 1}
                        page={(documentScansFilters.pagination?.page || 1) - 1}
                        onPageChange={(page: number) => handlePaginationFilterChange(page + 1)}
                        showing={documentScans.length}
                        totalRecords={totalCount}
                    />
                )}
            </PageContent>
        </PageContainer>
    );
}

export default observer(DocumentScans);
